(() => {
  const VERSION = '7.1.0';
  const DESKTOP_QUERY = '(min-width: 1150px)';
  const EXCLUDED_NAV =
    '.menu-drawer, header-drawer, .burger-drawer, [role="dialog"], .header-top__right';

  const records = new Set();

  const slugify = (value = '') =>
    String(value)
      .toLowerCase()
      .trim()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const normalizePath = (value = '') => {
    try {
      return new URL(value, window.location.origin).pathname.replace(/\/+$/, '') || '/';
    } catch (_error) {
      return String(value).split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
    }
  };

  const delayValue = (value, fallback) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : fallback;
  };

  const isVisible = (node) => {
    if (!node || node.closest(EXCLUDED_NAV)) return false;

    const style = window.getComputedStyle(node);

    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      node.getClientRects().length > 0
    );
  };

  const triggerText = (node) => {
    if (!node) return '';

    const clone = node.cloneNode(true);

    clone
      .querySelectorAll(
        'svg, .icon, .visually-hidden, .header__submenu, .submenu, .mega-menu, ul'
      )
      .forEach((element) => element.remove());

    return String(clone.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const isTopLevelTrigger = (node) => {
    const navigation = node.closest('.ns-header-menu, .header__inline-menu, nav');
    const item = node.closest('li');

    if (!navigation || !item || !navigation.contains(item)) return false;

    const parentItem = item.parentElement?.closest('li');

    return !parentItem || !navigation.contains(parentItem);
  };

  const getTriggerCandidates = (root) => {
    const selectors = [
      /*
       * New two-row header layout.
       * The navigation is rendered inside `.ns-header-menu`.
       */
      '.ns-header-menu nav > ul > li > a',
      '.ns-header-menu nav > ul > li > details > summary',
      '.ns-header-menu > ul > li > a',
      '.ns-header-menu > ul > li > details > summary',
      '.ns-header-menu .list-menu--inline > li > a',
      '.ns-header-menu .list-menu--inline > li > details > summary',

      /*
       * Original theme layouts remain supported.
       */
      '.header__inline-menu > ul > li > a',
      '.header__inline-menu > ul > li > details > summary',
      '.header__inline-menu .list-menu--inline > li > a',
      '.header__inline-menu .list-menu--inline > li > details > summary',
      '.header-top__left nav > ul > li > a',
      '.header-top__left nav > ul > li > details > summary',
      '.header-top__left .list-menu--inline > li > a',
      '.header-top__left .list-menu--inline > li > details > summary'
    ];

    const preferred = selectors.flatMap((selector) =>
      Array.from(root.querySelectorAll(selector))
    );

    const fallback = Array.from(
      root.querySelectorAll(
        [
          '.ns-header-menu a',
          '.ns-header-menu summary',
          '.header__inline-menu a',
          '.header__inline-menu summary',
          '.header-top__left nav a',
          '.header-top__left nav summary'
        ].join(', ')
      )
    );

    return [...new Set([...preferred, ...fallback])].filter(
      (candidate) => isVisible(candidate) && isTopLevelTrigger(candidate)
    );
  };

  const activatePanel = (megaMenu, panelId) => {
    const tabs = Array.from(megaMenu.querySelectorAll('[data-ns-mega-tab]'));
    const panels = Array.from(megaMenu.querySelectorAll('[data-ns-mega-panel]'));

    tabs.forEach((tab) => {
      const active = tab.dataset.nsMegaTab === panelId;

      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
    });

    panels.forEach((panel) => {
      const active = panel.id === panelId;

      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    });
  };

  const setupTabs = (megaMenu) => {
    if (megaMenu.dataset.nsTabsReady === VERSION) return;

    megaMenu.dataset.nsTabsReady = VERSION;

    const tabs = Array.from(megaMenu.querySelectorAll('[data-ns-mega-tab]'));

    if (!tabs.length) return;

    tabs.forEach((tab, index) => {
      const openPanel = () => activatePanel(megaMenu, tab.dataset.nsMegaTab);

      tab.addEventListener('mouseenter', openPanel);
      tab.addEventListener('focus', openPanel);

      tab.addEventListener('click', (event) => {
        if (tab.classList.contains('is-active')) return;

        event.preventDefault();
        openPanel();
      });

      tab.addEventListener('keydown', (event) => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;

        event.preventDefault();

        let nextIndex = index;

        if (event.key === 'ArrowDown') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowUp') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;

        tabs[nextIndex].focus();
      });
    });

    activatePanel(megaMenu, tabs[0].dataset.nsMegaTab);
  };

  const closeRecord = (record, restoreFocus = false) => {
    if (!record) return;

    window.clearTimeout(record.openTimer);
    window.clearTimeout(record.closeTimer);

    record.megaMenu.classList.remove('is-open');
    record.megaMenu.setAttribute('aria-hidden', 'true');
    record.trigger.setAttribute('aria-expanded', 'false');
    record.root.classList.remove('ns-product-mega-is-open');

    if (restoreFocus) record.trigger.focus();
  };

  const closeAll = (exceptRecord = null) => {
    records.forEach((record) => {
      if (record !== exceptRecord) closeRecord(record);
    });
  };

  const openRecord = (record) => {
    closeAll(record);

    window.clearTimeout(record.openTimer);
    window.clearTimeout(record.closeTimer);

    const details = record.trigger.closest('details');

    if (details) details.removeAttribute('open');

    record.megaMenu.classList.add('is-open');
    record.megaMenu.setAttribute('aria-hidden', 'false');
    record.trigger.setAttribute('aria-expanded', 'true');
    record.root.classList.add('ns-product-mega-is-open');
  };

  const suppressOriginalSubmenu = (trigger) => {
    const item = trigger.closest('li');

    if (!item) return null;

    item.classList.add('ns-product-mega__trigger-item');

    const details = trigger.closest('details');

    if (details) {
      details.removeAttribute('open');
      details.dataset.nsProductMegaDetails = VERSION;
    }

    item
      .querySelectorAll(
        ':scope > .header__submenu, :scope > .submenu, :scope > .mega-menu, :scope > details > .header__submenu, :scope > details > .submenu, :scope > details > .list-menu--disclosure'
      )
      .forEach((submenu) => {
        submenu.dataset.nsOriginalSubmenu = VERSION;
        submenu.hidden = true;
      });

    return item;
  };

  const findTrigger = (megaMenu, candidates) => {
    const expectedHandle = megaMenu.dataset.nsParentHandle || '';
    const expectedTitle = String(megaMenu.dataset.nsParentTitle || '')
      .trim()
      .toLowerCase();
    const expectedPath = normalizePath(megaMenu.dataset.nsParentUrl || '');

    return candidates.find((candidate) => {
      const text = triggerText(candidate);
      const titleMatches =
        slugify(text) === expectedHandle || text.toLowerCase() === expectedTitle;

      const href =
        candidate.tagName === 'A'
          ? candidate.getAttribute('href')
          : candidate.querySelector('a[href]')?.getAttribute('href');

      const urlMatches =
        href && expectedPath !== '/' && normalizePath(href) === expectedPath;

      return titleMatches || urlMatches;
    });
  };

  const mountMegaMenu = (root, megaMenu, candidates) => {
    if (megaMenu.dataset.nsMegaMounted === VERSION) return;

    const trigger = findTrigger(megaMenu, candidates);

    if (!trigger) {
      console.warn('[Product mega menu] Top-level menu item was not found.', {
        expectedTitle: megaMenu.dataset.nsParentTitle,
        expectedHandle: megaMenu.dataset.nsParentHandle,
        expectedUrl: megaMenu.dataset.nsParentUrl,
        availableItems: candidates.map((candidate) => triggerText(candidate))
      });

      return;
    }

    megaMenu.dataset.nsMegaMounted = VERSION;
    trigger.dataset.nsMegaTrigger = VERSION;

    trigger.setAttribute('aria-haspopup', 'true');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', megaMenu.id);
    megaMenu.setAttribute('aria-hidden', 'true');

    const triggerItem = suppressOriginalSubmenu(trigger);

    setupTabs(megaMenu);

    const openMode = megaMenu.dataset.nsOpenMode || 'hover_click';
    const hoverEnabled = openMode === 'hover' || openMode === 'hover_click';
    const clickEnabled = openMode === 'click' || openMode === 'hover_click';

    const record = {
      root,
      megaMenu,
      trigger,
      triggerItem,
      openDelay: delayValue(megaMenu.dataset.nsHoverOpenDelay, 100),
      closeDelay: delayValue(megaMenu.dataset.nsHoverCloseDelay, 500),
      openTimer: 0,
      closeTimer: 0
    };

    records.add(record);

    /*
     * Do not require `(pointer: fine)`.
     * On Windows touch laptops and some hybrid devices the primary pointer is
     * reported as coarse even while a mouse is being used, which prevented the
     * hover handler from running.
     */
    const canHover = () => window.matchMedia(DESKTOP_QUERY).matches;
    const hoverTarget = triggerItem || trigger;

    const isPointerOrFocusInside = () => {
      const active = document.activeElement;

      return (
        hoverTarget.matches(':hover') ||
        megaMenu.matches(':hover') ||
        hoverTarget.contains(active) ||
        megaMenu.contains(active)
      );
    };

    const scheduleOpen = () => {
      window.clearTimeout(record.closeTimer);
      window.clearTimeout(record.openTimer);

      record.openTimer = window.setTimeout(() => openRecord(record), record.openDelay);
    };

    const scheduleClose = () => {
      window.clearTimeout(record.openTimer);
      window.clearTimeout(record.closeTimer);

      record.closeTimer = window.setTimeout(() => {
        // Re-check after the delay. This prevents the menu from closing while
        // the pointer is travelling from the top-level trigger into the mega menu.
        if (canHover() && isPointerOrFocusInside()) return;

        closeRecord(record);
      }, record.closeDelay);
    };

    record.scheduleOpen = scheduleOpen;
    record.scheduleClose = scheduleClose;
    record.canHover = canHover;

    if (hoverEnabled) {

      /*
       * mouseenter handles normal desktop browsers.
       * pointerenter and the delegated mouseover fallback below cover theme
       * overlays, hybrid touch devices and navigation markup that is replaced
       * after initial page load.
       */
      hoverTarget.addEventListener('mouseenter', () => {
        if (canHover()) scheduleOpen();
      });

      hoverTarget.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        if (canHover()) scheduleOpen();
      });

      hoverTarget.addEventListener('mouseleave', (event) => {
        if (!canHover()) return;
        if (megaMenu.contains(event.relatedTarget)) return;

        scheduleClose();
      });

      hoverTarget.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        if (!canHover()) return;
        if (megaMenu.contains(event.relatedTarget)) return;

        scheduleClose();
      });

      megaMenu.addEventListener('mouseenter', () => {
        window.clearTimeout(record.closeTimer);
      });

      megaMenu.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        window.clearTimeout(record.closeTimer);
      });

      megaMenu.addEventListener('mouseleave', (event) => {
        if (!canHover()) return;

        if (
          triggerItem?.contains(event.relatedTarget) ||
          trigger.contains(event.relatedTarget)
        ) {
          return;
        }

        scheduleClose();
      });

      megaMenu.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        if (!canHover()) return;

        if (
          triggerItem?.contains(event.relatedTarget) ||
          trigger.contains(event.relatedTarget)
        ) {
          return;
        }

        scheduleClose();
      });
    }

    trigger.addEventListener(
      'click',
      (event) => {
        if (!clickEnabled) return;

        event.preventDefault();
        event.stopPropagation();

        if (megaMenu.classList.contains('is-open')) {
          closeRecord(record);
        } else {
          openRecord(record);
        }

        window.setTimeout(() => {
          const details = trigger.closest('details');

          if (details) details.removeAttribute('open');
        }, 0);
      },
      true
    );

    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        openRecord(record);
        megaMenu.querySelector('[data-ns-mega-tab]')?.focus();
      } else if ((event.key === 'Enter' || event.key === ' ') && clickEnabled) {
        event.preventDefault();

        if (megaMenu.classList.contains('is-open')) {
          closeRecord(record);
        } else {
          openRecord(record);
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeRecord(record, true);
      }
    });

    megaMenu.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      event.preventDefault();
      closeRecord(record, true);
    });

    megaMenu.addEventListener('focusin', () => {
      window.clearTimeout(record.closeTimer);
    });

    megaMenu.addEventListener('focusout', (event) => {
      if (
        !megaMenu.contains(event.relatedTarget) &&
        event.relatedTarget !== trigger &&
        !triggerItem?.contains(event.relatedTarget)
      ) {
        scheduleClose();
      }
    });
  };

  const mountRoot = (root) => {
    if (!root) return;

    const candidates = getTriggerCandidates(root);
    const megaMenus = Array.from(root.querySelectorAll('[data-ns-product-mega]'));

    megaMenus.forEach((megaMenu) => mountMegaMenu(root, megaMenu, candidates));
  };

  const mount = (scope = document) => {
    const roots = new Set();

    if (scope.matches?.('.shopify-section-header')) roots.add(scope);

    scope
      .querySelectorAll?.('.shopify-section-header')
      .forEach((root) => roots.add(root));

    if (!roots.size) {
      document
        .querySelectorAll('.shopify-section-header')
        .forEach((root) => roots.add(root));
    }

    roots.forEach(mountRoot);
  };

  /*
   * Capturing fallback: some themes place absolutely-positioned elements over
   * the navigation link. `mouseover` still reaches the document even when a
   * direct mouseenter listener is skipped.
   */
  document.addEventListener(
    'mouseover',
    (event) => {
      if (!window.matchMedia(DESKTOP_QUERY).matches) return;

      records.forEach((record) => {
        const hoverTarget = record.triggerItem || record.trigger;
        if (!hoverTarget || !hoverTarget.contains(event.target)) return;
        if (event.relatedTarget && hoverTarget.contains(event.relatedTarget)) return;

        record.scheduleOpen?.();
      });
    },
    true
  );

  document.addEventListener(
    'mouseout',
    (event) => {
      if (!window.matchMedia(DESKTOP_QUERY).matches) return;

      records.forEach((record) => {
        const hoverTarget = record.triggerItem || record.trigger;
        if (!hoverTarget || !hoverTarget.contains(event.target)) return;

        const nextTarget = event.relatedTarget;
        if (
          nextTarget &&
          (hoverTarget.contains(nextTarget) || record.megaMenu.contains(nextTarget))
        ) {
          return;
        }

        record.scheduleClose?.();
      });
    },
    true
  );

  document.addEventListener('click', (event) => {
    records.forEach((record) => {
      if (!record.megaMenu.classList.contains('is-open')) return;

      if (
        record.megaMenu.contains(event.target) ||
        record.trigger.contains(event.target) ||
        record.triggerItem?.contains(event.target)
      ) {
        return;
      }

      closeRecord(record);
    });
  });

  window.addEventListener('resize', () => {
    if (!window.matchMedia(DESKTOP_QUERY).matches) closeAll();
  });

  /*
   * The Theme Editor and some header scripts can replace the desktop
   * navigation after DOMContentLoaded. Remount when that happens.
   */
  let remountTimer = 0;

  const scheduleRemount = () => {
    window.clearTimeout(remountTimer);
    remountTimer = window.setTimeout(() => mount(document), 80);
  };

  const headerObserver = new MutationObserver((mutations) => {
    const navigationChanged = mutations.some((mutation) =>
      Array.from(mutation.addedNodes || []).some(
        (node) =>
          node.nodeType === 1 &&
          (
            node.matches?.(
              '.ns-header-menu, .header__inline-menu, [data-ns-product-mega], .ns-product-mega-root'
            ) ||
            node.querySelector?.(
              '.ns-header-menu, .header__inline-menu, [data-ns-product-mega], .ns-product-mega-root'
            )
          )
      )
    );

    if (navigationChanged) scheduleRemount();
  });

  const observeHeader = () => {
    document.querySelectorAll('.shopify-section-header').forEach((header) => {
      if (header.dataset.nsMegaObserved === VERSION) return;

      header.dataset.nsMegaObserved = VERSION;
      headerObserver.observe(header, {
        childList: true,
        subtree: true
      });
    });
  };

  window.NSProductMegaMenu = {
    version: VERSION,
    mount,
    closeAll
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        observeHeader();
        mount(document);
      },
      { once: true }
    );
  } else {
    observeHeader();
    mount(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    window.setTimeout(() => {
      observeHeader();
      mount(event.target);
    }, 0);
  });

  document.addEventListener('shopify:section:reorder', () => {
    window.setTimeout(() => mount(document), 0);
  });

  document.addEventListener('shopify:section:unload', () => closeAll());
})();
